/**
 * ProgressBar — 재생 진행률 바.
 * - track 6px, fill lightblue/900, picker 20px
 * - track/handle 포인터 드래그로 재생 위치 변경
 * - skipPolicy 게이팅: forward seek 차단 시 onSeekBlocked 호출
 * - maxScrubSec: 활성 Pause point 동안 scrub 상한 (정의된 경우 clamp 후 게이팅)
 */
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  currentPercent: number
  durationSec: number
  canSeek: boolean
  getMaxProgressSec: () => number
  onSeek: (targetSec: number) => void
  onSeekBlocked: () => void
  maxScrubSec?: number
  pausePoints?: ReadonlyArray<{
    time: number
    status: 'pending' | 'completed'
  }>
  appearance?: 'legacy' | 'v3'
}

interface ScrubPreview {
  seconds: number
  percent: number
}

function formatTime(sec: number): string {
  const seconds = Math.max(0, Math.floor(sec))
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`
}

export default function ProgressBar({
  currentPercent,
  durationSec,
  canSeek,
  getMaxProgressSec,
  onSeek,
  onSeekBlocked,
  maxScrubSec,
  pausePoints,
  appearance = 'legacy',
}: Props) {
  const { t } = useTranslation('learner')
  const trackRef = useRef<HTMLDivElement>(null)
  const activePointerIdRef = useRef<number | null>(null)
  const blockedNotifiedRef = useRef(false)
  const pendingTargetSecRef = useRef<number | null>(null)
  const [scrubPreview, setScrubPreview] = useState<ScrubPreview | null>(null)

  const updateScrubPreview = useCallback((clientX: number) => {
    const track = trackRef.current
    if (!track || durationSec <= 0) return
    const rect = track.getBoundingClientRect()
    if (rect.width <= 0) return
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    let targetSec = ratio * durationSec
    if (maxScrubSec != null) {
      targetSec = Math.min(targetSec, maxScrubSec)
    }
    const maxSec = getMaxProgressSec()

    if (targetSec <= maxSec || canSeek) {
      pendingTargetSecRef.current = targetSec
      setScrubPreview({
        seconds: targetSec,
        percent: (targetSec / durationSec) * 100,
      })
    } else {
      pendingTargetSecRef.current = null
      setScrubPreview(null)
      if (!blockedNotifiedRef.current) {
        blockedNotifiedRef.current = true
        onSeekBlocked()
      }
    }
  }, [durationSec, canSeek, getMaxProgressSec, onSeekBlocked, maxScrubSec])

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (durationSec <= 0 || e.button !== 0) return
    activePointerIdRef.current = e.pointerId
    blockedNotifiedRef.current = false
    pendingTargetSecRef.current = null
    e.currentTarget.setPointerCapture?.(e.pointerId)
    updateScrubPreview(e.clientX)
  }, [durationSec, updateScrubPreview])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return
    updateScrubPreview(e.clientX)
  }, [updateScrubPreview])

  const finishDragging = useCallback((e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
    if (activePointerIdRef.current !== e.pointerId) return
    const targetSec = pendingTargetSecRef.current
    activePointerIdRef.current = null
    blockedNotifiedRef.current = false
    pendingTargetSecRef.current = null
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    }
    if (commit && targetSec != null) {
      onSeek(targetSec)
    }
    setScrubPreview(null)
  }, [onSeek])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    finishDragging(e, true)
  }, [finishDragging])

  const handlePointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    finishDragging(e, false)
  }, [finishDragging])

  const handleLostPointerCapture = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    finishDragging(e, true)
  }, [finishDragging])

  const clampedCurrent = Math.max(0, Math.min(100, currentPercent))
  const displayPercent = scrubPreview?.percent ?? clampedCurrent
  const showCapMarker = maxScrubSec != null && durationSec > 0
  const capPercent = showCapMarker ? Math.max(0, Math.min(100, (maxScrubSec / durationSec) * 100)) : 0

  return (
    <div
      ref={trackRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={handleLostPointerCapture}
      className={`group/progress relative h-[6px] flex-1 cursor-pointer touch-none select-none rounded-[50px] ${
        appearance === 'v3' ? 'bg-lc-v3-gray-300' : 'bg-lc-gray-200'
      }`}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={maxScrubSec != null && durationSec > 0
        ? Math.round((maxScrubSec / durationSec) * 100)
        : 100}
      aria-valuenow={Math.round(displayPercent)}
      aria-label="재생 진행률"
    >
      <div
        data-testid="video-progress-track-clip"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[50px]"
      >
        <div
          className={`absolute left-0 top-0 h-full w-full origin-left will-change-transform ${
            appearance === 'v3' ? 'bg-lc-key-500' : 'bg-lc-lightblue-900'
          }`}
          style={{ transform: `scaleX(${displayPercent / 100})` }}
        />

        {showCapMarker && (
          <div
            className="absolute top-0 h-full bg-lc-v3-gray-300 opacity-60"
            style={{ left: `${capPercent}%`, width: `${100 - capPercent}%` }}
            aria-label={t('pause.scrubCapHint') as string}
          />
        )}
      </div>

      {durationSec > 0 && pausePoints?.map((pt, i) => {
        const pct = Math.max(0, Math.min(100, (pt.time / 1000 / durationSec) * 100))
        const colorClass = pt.status === 'completed' ? 'bg-lc-key-500' : 'bg-lc-v3-gray-500'
        const statusLabel = pt.status === 'completed'
          ? t('pause.markerCompleted')
          : t('pause.markerPending')
        return (
          <div
            key={`pp-${pt.time}-${i}`}
            role="img"
            aria-label={`${i + 1}번 문제 — ${statusLabel}`}
            className={`absolute top-[-4px] w-[2px] h-[14px] ${colorClass} rounded-[1px] pointer-events-none`}
            style={{ left: `calc(${pct}% - 1px)` }}
          />
        )
      })}

      {scrubPreview && (
        <div
          data-testid="video-progress-preview"
          className="absolute bottom-[16px] z-10 -translate-x-1/2 rounded-md bg-lc-gray-900 px-2 py-1 text-xs font-semibold tabular-nums text-white shadow-md pointer-events-none"
          style={{ left: `${displayPercent}%` }}
        >
          {formatTime(scrubPreview.seconds)}
        </div>
      )}

      <div
        data-testid="video-progress-handle"
        aria-hidden
        className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-[width,height,box-shadow] duration-150 will-change-[left] ${
          appearance === 'v3'
            ? 'size-4 bg-lc-key-500'
            : scrubPreview
              ? 'size-6 cursor-grabbing border-2 border-white bg-lc-lightblue-900 shadow-[0_0_0_5px_rgba(75,190,243,0.2)]'
              : 'size-5 cursor-grab border-2 border-white bg-lc-lightblue-900 shadow-[0_2px_0_rgba(0,0,0,0.1)] group-hover/progress:size-6'
        }`}
        style={{ left: `${displayPercent}%` }}
      />
    </div>
  )
}
